import styles from './UserAvatarCarousel.module.scss'
import {useEffect, useMemo, useState} from 'react'
import {WuIcon} from '@npm-questionpro/wick-ui-lib'
import {DIALOG_AVATAR_IMAGE_URL} from '../../../../constants/dialogConstants'
import {getHexFromColorText} from '../../../../utils'

interface IUserAvatarCarouselProps {
  usernames: string[] | undefined
  onUsernameChange: (username: string | undefined) => void
}

export const UserAvatarCarousel = ({
  usernames,
  onUsernameChange,
}: IUserAvatarCarouselProps): React.ReactElement => {
  const [currentIndex, setCurrentIndex] = useState(1)
  const [isAnimating, setIsAnimating] = useState(true)

  const visibleItems = 1
  const loopedItems = useMemo(() => {
    if (!usernames || usernames.length === 0) return []
    return [usernames[usernames.length - 1], ...usernames, usernames[0]]
  }, [usernames])

  const next = (): void => {
    setIsAnimating(true)
    setCurrentIndex(prev => prev + 1)
  }

  const prev = (): void => {
    setIsAnimating(true)
    setCurrentIndex(prev => prev - 1)
  }

  const onTrackTransitionEnd = (): void => {
    if (!usernames || usernames.length === 0) return

    if (currentIndex === loopedItems.length - 1) {
      setIsAnimating(false)
      setCurrentIndex(1)
    }

    if (currentIndex === 0) {
      setIsAnimating(false)
      setCurrentIndex(usernames.length)
    }
  }

  useEffect(() => {
    if (!usernames || usernames.length === 0) return
    const selected =
      usernames[(currentIndex - 1 + usernames.length) % usernames.length]
    onUsernameChange?.(selected)
  }, [currentIndex, usernames, onUsernameChange])

  useEffect(() => {
    if (isAnimating) return

    const frame = requestAnimationFrame(() => setIsAnimating(true))
    return (): void => cancelAnimationFrame(frame)
  }, [isAnimating])

  return (
    <div className={styles.userCarousel}>
      <WuIcon
        icon="wm-chevron-left"
        className={styles.userCarouselBtn}
        onClick={prev}
      />

      <div className={styles.userCarouselViewport}>
        <div
          onTransitionEnd={onTrackTransitionEnd}
          className={styles.userCarouselBlock}
          style={{
            transition: isAnimating ? 'transform 0.5s ease' : 'none',
            transform: `translateX(-${(currentIndex * 100) / visibleItems}%)`,
          }}
        >
          {loopedItems.map((item, index) => (
            <div
              key={index}
              className={styles.carouselItem}
              style={{flex: `0 0 ${100 / visibleItems}%`}}
            >
              <div className={styles.carouselItemLabel}>
                <img
                  src={`${DIALOG_AVATAR_IMAGE_URL}${item}`}
                  alt="coverImage"
                  width={'100%'}
                  height={'100%'}
                  loading="lazy"
                  className={styles.avatarImg}
                  style={{
                    backgroundColor: getHexFromColorText(item.split(' ')[0]),
                  }}
                />
                <div className={styles.username}>{item}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <WuIcon
        icon="wm-chevron-right"
        className={styles.userCarouselBtn}
        onClick={next}
      />
    </div>
  )
}
